import { useState, useEffect, useMemo } from 'react';
import { X, GitCompare, FileText, Check } from 'lucide-react';

/**
 * Generic item for comparison. Works with system prompts now, reusable for other text comparisons later.
 */
export interface ComparisonItem {
  id: string;
  label: string;
  description?: string;
  content: string;
  createdAt?: string;
}

interface TextComparisonDialogProps {
  isOpen: boolean;
  title: string;
  items: ComparisonItem[];
  initialLeftId?: string;
  initialRightId?: string;
  /** When set, the left selector is locked to this id and cannot be changed. */
  lockedLeftId?: string;
  /** Fetches full content for an item by id. Called lazily when item is selected. */
  fetchContent?: (id: string) => Promise<string>;
  onApprove: (id: string) => void;
  onClose: () => void;
  /** When true, hides all approve/use-this buttons. */
  hideApprove?: boolean;
}

type DiffLine = {
  type: 'same' | 'added' | 'removed';
  lineNumber: { left?: number; right?: number };
  text: string;
};

/**
 * Simple line-based diff using longest common subsequence.
 */
function computeDiff(leftText: string, rightText: string): DiffLine[] {
  const leftLines = leftText.split('\n');
  const rightLines = rightText.split('\n');

  // LCS table
  const m = leftLines.length;
  const n = rightLines.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (leftLines[i - 1] === rightLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to build diff
  const result: DiffLine[] = [];
  let i = m;
  let j = n;

  const stack: DiffLine[] = [];
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && leftLines[i - 1] === rightLines[j - 1]) {
      stack.push({ type: 'same', lineNumber: { left: i, right: j }, text: leftLines[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ type: 'added', lineNumber: { right: j }, text: rightLines[j - 1] });
      j--;
    } else {
      stack.push({ type: 'removed', lineNumber: { left: i }, text: leftLines[i - 1] });
      i--;
    }
  }

  // Reverse since we built it backwards
  for (let k = stack.length - 1; k >= 0; k--) {
    result.push(stack[k]);
  }

  return result;
}

export const TextComparisonDialog = ({
  isOpen,
  title,
  items,
  initialLeftId,
  initialRightId,
  lockedLeftId,
  fetchContent,
  onApprove,
  onClose,
  hideApprove = false
}: TextComparisonDialogProps) => {
  const [leftId, setLeftId] = useState<string>('');
  const [rightId, setRightId] = useState<string>('');
  const [showDiff, setShowDiff] = useState(false);
  const [leftContent, setLeftContent] = useState<string>('');
  const [rightContent, setRightContent] = useState<string>('');
  const [loadingLeft, setLoadingLeft] = useState(false);
  const [loadingRight, setLoadingRight] = useState(false);

  // Reset state when dialog opens — no auto-selection, user picks versions
  useEffect(() => {
    if (isOpen) {
      setLeftId(initialLeftId || '');
      setRightId(initialRightId || '');
      setShowDiff(false);
      setLeftContent('');
      setRightContent('');
    }
  }, [isOpen, initialLeftId, initialRightId]);

  const loadContent = (id: string, setSide: (c: string) => void, setLoading: (l: boolean) => void) => {
    if (!id) { setSide(''); return; }
    const item = items.find(i => i.id === id);
    if (item?.content) {
      setSide(item.content);
    } else if (fetchContent) {
      setLoading(true);
      fetchContent(id)
        .then(content => setSide(content))
        .catch(() => setSide('[Failed to load content]'))
        .finally(() => setLoading(false));
    }
  };

  const handleLeftChange = (id: string) => {
    setLeftId(id);
    setLeftContent('');
    loadContent(id, setLeftContent, setLoadingLeft);
  };

  const handleRightChange = (id: string) => {
    setRightId(id);
    setRightContent('');
    loadContent(id, setRightContent, setLoadingRight);
  };

  // Load initial left content only if initialLeftId was provided
  useEffect(() => {
    if (isOpen && initialLeftId) {
      loadContent(initialLeftId, setLeftContent, setLoadingLeft);
    }
    if (isOpen && initialRightId) {
      loadContent(initialRightId, setRightContent, setLoadingRight);
    }
  }, [isOpen, initialLeftId, initialRightId]);

  const diffLines = useMemo(() => {
    if (!showDiff || !leftContent || !rightContent) return [];
    return computeDiff(leftContent, rightContent);
  }, [showDiff, leftContent, rightContent]);

  const leftItem = items.find(i => i.id === leftId);
  const rightItem = items.find(i => i.id === rightId);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[60]" onClick={onClose} />
      <div data-component="TextComparisonDialog" className="fixed inset-0 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDiff(!showDiff)}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                  showDiff
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                disabled={!leftId || !rightId || leftId === rightId}
                title={leftId === rightId ? 'Select two different versions to compare' : 'Toggle diff view'}
              >
                <GitCompare className="w-4 h-4" />
                {showDiff ? 'Hide Diff' : 'Show Diff'}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Version Selectors */}
          <div className="px-6 py-3 border-b border-gray-200 flex-shrink-0">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {showDiff ? 'Base (Left)' : 'Version A'}
                </label>
                <select
                  value={leftId}
                  onChange={(e) => handleLeftChange(e.target.value)}
                  disabled={!!lockedLeftId}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm${lockedLeftId ? ' bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select version...</option>
                  {items.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.label}{item.createdAt ? ` (${new Date(item.createdAt).toLocaleDateString()})` : ''}
                    </option>
                  ))}
                </select>
                {leftItem?.description && (
                  <p className="text-xs text-gray-500 mt-1 truncate" title={leftItem.description}>
                    {leftItem.description}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {showDiff ? 'Compare (Right)' : 'Version B'}
                </label>
                <select
                  value={rightId}
                  onChange={(e) => handleRightChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">Select version...</option>
                  {items.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.label}{item.createdAt ? ` (${new Date(item.createdAt).toLocaleDateString()})` : ''}
                    </option>
                  ))}
                </select>
                {rightItem?.description && (
                  <p className="text-xs text-gray-500 mt-1 truncate" title={rightItem.description}>
                    {rightItem.description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-auto px-6 py-4 min-h-0">
            {loadingLeft || loadingRight ? (
              <div data-flow="loading" className="flex items-center justify-center h-64 text-gray-500">
                Loading content...
              </div>
            ) : showDiff && leftId && rightId && leftId !== rightId ? (
              /* Unified Diff View */
              <div data-flow="diff-view" className="font-mono text-sm border border-gray-200 rounded-lg overflow-auto">
                <table className="w-full border-collapse">
                  <tbody>
                    {diffLines.map((line, idx) => (
                      <tr
                        key={idx}
                        className={
                          line.type === 'added'
                            ? 'bg-green-50'
                            : line.type === 'removed'
                            ? 'bg-red-50'
                            : ''
                        }
                      >
                        <td className="px-2 py-0.5 text-right text-gray-400 select-none border-r border-gray-200 w-12 text-xs">
                          {line.lineNumber.left || ''}
                        </td>
                        <td className="px-2 py-0.5 text-right text-gray-400 select-none border-r border-gray-200 w-12 text-xs">
                          {line.lineNumber.right || ''}
                        </td>
                        <td className="px-2 py-0.5 select-none w-6 text-center">
                          {line.type === 'added' && <span className="text-green-600 font-bold">+</span>}
                          {line.type === 'removed' && <span className="text-red-600 font-bold">−</span>}
                        </td>
                        <td className="px-2 py-0.5 whitespace-pre-wrap break-all">
                          <span className={
                            line.type === 'added'
                              ? 'text-green-800'
                              : line.type === 'removed'
                              ? 'text-red-800'
                              : 'text-gray-800'
                          }>
                            {line.text}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Side-by-Side View */
              <div data-flow="side-by-side" className="grid grid-cols-2 gap-4 h-full">
                <div className="flex flex-col min-h-0">
                  <div className="text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
                    <span>
                      {leftItem?.label || 'Select a version'}
                      {leftItem?.createdAt && (
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          {new Date(leftItem.createdAt).toLocaleDateString()}
                        </span>
                      )}
                    </span>
                    {leftId && !hideApprove && (
                      <button
                        onClick={() => onApprove(leftId)}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        <Check className="w-3 h-3" />
                        Use This
                      </button>
                    )}
                  </div>
                  <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-auto min-h-[300px]">
                    <pre className="text-sm text-gray-100 whitespace-pre-wrap font-mono">
                      {leftContent || (leftId ? '' : 'Select a version to view content')}
                    </pre>
                  </div>
                </div>
                <div className="flex flex-col min-h-0">
                  <div className="text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
                    <span>
                      {rightItem?.label || 'Select a version'}
                      {rightItem?.createdAt && (
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          {new Date(rightItem.createdAt).toLocaleDateString()}
                        </span>
                      )}
                    </span>
                    {rightId && !hideApprove && (
                      <button
                        onClick={() => onApprove(rightId)}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        <Check className="w-3 h-3" />
                        Use This
                      </button>
                    )}
                  </div>
                  <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-auto min-h-[300px]">
                    <pre className="text-sm text-gray-100 whitespace-pre-wrap font-mono">
                      {rightContent || (rightId ? '' : 'Select a version to view content')}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer with approve buttons in diff mode */}
          {showDiff && leftId && rightId && leftId !== rightId && (
            <div className="px-6 py-3 border-t border-gray-200 flex justify-between items-center flex-shrink-0 bg-gray-50">
              <div className="text-sm text-gray-600">
                <span className="inline-block w-3 h-3 bg-red-100 border border-red-300 rounded mr-1"></span> Removed
                <span className="inline-block w-3 h-3 bg-green-100 border border-green-300 rounded ml-3 mr-1"></span> Added
              </div>
              {!hideApprove && (
                <div className="flex gap-2">
                  <button
                    onClick={() => onApprove(leftId)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Check className="w-4 h-4" />
                    Use {leftItem?.label || 'Left'}{leftItem?.createdAt ? ` (${new Date(leftItem.createdAt).toLocaleDateString()})` : ''}
                  </button>
                  <button
                    onClick={() => onApprove(rightId)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Check className="w-4 h-4" />
                    Use {rightItem?.label || 'Right'}{rightItem?.createdAt ? ` (${new Date(rightItem.createdAt).toLocaleDateString()})` : ''}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
